/**
 * CustomerDetailPage
 * Detailed view of an individual taxpayer customer with tabs
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  MoreHorizontal,
  Archive,
  Trash2,
  Mail,
  Phone,
  User,
  FileText,
  FolderOpen,
  Bell,
  MessageSquare,
  Clock,
  UserCheck,
} from 'lucide-react'
import { useCustomerStore } from '@/store/customerStore'
import CustomerModal from '@/components/customers/CustomerModal'
import AssignCustomerModal from '@/components/customers/AssignCustomerModal'
import CustomerOverviewTab from '@/components/customers/tabs/CustomerOverviewTab'
import CustomerAssessmentsTab from '@/components/customers/tabs/CustomerAssessmentsTab'
import CustomerInsightsSection from '@/components/customers/CustomerInsightsSection'
import CustomerDocumentsTab from '@/components/customers/tabs/CustomerDocumentsTab'
import CustomerRemindersTab from '@/components/customers/tabs/CustomerRemindersTab'
import CustomerNotesTab from '@/components/customers/tabs/CustomerNotesTab'
import CustomerActivityTab from '@/components/customers/tabs/CustomerActivityTab'
import { canAccessRoute } from '@/security/uiAccess'
import { useAuth } from '@/contexts/AuthContext'

type TabId = 'overview' | 'assessments' | 'documents' | 'reminders' | 'notes' | 'activity'

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'assessments', label: 'Assessments', icon: FileText },
  { id: 'documents', label: 'Documents', icon: FolderOpen },
  { id: 'reminders', label: 'Reminders', icon: Bell },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'activity', label: 'Activity Log', icon: Clock },
]

export default function CustomerDetailPage() {
  const { user, hasPermission, canAccessFeature } = useAuth()
  const canUpdate = hasPermission('txh.client.update')
  const canDelete = hasPermission('txh.client.delete')
  const canAssign = canUpdate && hasPermission('txh.user.read')
  const canReadAssessments =
    canAccessRoute(user, 'assessmentDetail') || canAccessRoute(user, 'returnCases')
  const canReadDocuments = hasPermission('txh.document.read') && canAccessFeature('documents')
  const canReadReminders = hasPermission('txh.reminder.read')
  const canReadNotes = hasPermission('txh.client.note.read')
  const canReadActivity = canAccessRoute(user, 'audit')
  const canReadInsights = hasPermission('txh.insight.read')
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  // When set, the matching tab opens its create flow once on mount (driven by the
  // Overview "Quick Actions" buttons), then clears itself via onAutoCreateHandled.
  const [pendingCreate, setPendingCreate] = useState<TabId | null>(null)

  const { getCustomerById, archiveCustomer, deleteCustomer, customers, isLoading, loadCustomers } =
    useCustomerStore()

  const customer = customerId ? getCustomerById(customerId) : undefined

  // Ensure customers are loaded (e.g. on a direct navigation / refresh to this page)
  useEffect(() => {
    if (customers.length === 0) {
      loadCustomers()
    }
  }, [customers.length, loadCustomers])

  // Open edit modal if query param is set
  useEffect(() => {
    if (canUpdate && searchParams.get('edit') === 'true') {
      setIsEditModalOpen(true)
    }
  }, [searchParams])

  if (!customerId) {
    return (
      <div className="card card-elevated p-12 text-center">
        <p className="text-text-secondary">Invalid customer ID</p>
        <button onClick={() => navigate('/customers')} className="btn btn-primary btn-sm mt-4">
          Back to Customers
        </button>
      </div>
    )
  }

  if (!customer && isLoading) {
    return (
      <div className="card card-elevated p-12 text-center">
        <p className="text-text-secondary">Loading customer…</p>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="card card-elevated p-12 text-center">
        <p className="text-text-secondary">Customer not found</p>
        <button onClick={() => navigate('/customers')} className="btn btn-primary btn-sm mt-4">
          Back to Customers
        </button>
      </div>
    )
  }

  const handleArchive = async () => {
    if (!canUpdate) return
    if (window.confirm(`Are you sure you want to archive ${customer.fullName}?`)) {
      await archiveCustomer(customer.id)
      navigate('/customers')
    }
  }

  const handleDelete = async () => {
    if (!canDelete) return
    const reason = window
      .prompt(
        `Delete ${customer.fullName}? The record will be recoverable from the Recycle Bin for 30 days. Enter a reason:`
      )
      ?.trim()
    if (!reason) return
    await deleteCustomer(customer.id, reason)
    navigate('/customers')
  }

  const getStatusBadge = (status: typeof customer.status) => {
    const styles = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
      archived: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const getOccupationLabel = (occupation: typeof customer.occupation) => {
    const labels = {
      salaried: 'Salaried',
      self_employed: 'Self Employed',
      business: 'Business',
      retired: 'Retired',
      other: 'Other',
    }
    return labels[occupation]
  }

  // Overview "Quick Actions": jump to the relevant tab and flag it to open its
  // create flow. The flag clears once the target tab consumes it.
  const handleQuickAction = (tab: TabId) => {
    const permitted =
      (tab === 'assessments' &&
        canReadAssessments &&
        (hasPermission('txh.assessment.create') || hasPermission('txh.assessment.case.create'))) ||
      (tab === 'documents' && canReadDocuments && hasPermission('txh.document.upload')) ||
      (tab === 'reminders' && hasPermission('txh.reminder.create')) ||
      (tab === 'notes' && hasPermission('txh.client.note.create'))
    if (!permitted) return
    setActiveTab(tab)
    setPendingCreate(tab)
  }
  const clearPendingCreate = () => setPendingCreate(null)

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <CustomerOverviewTab
              customer={customer}
              onEdit={canUpdate ? () => setIsEditModalOpen(true) : undefined}
              onQuickAction={handleQuickAction}
              onManageAssignment={() => setIsAssignModalOpen(true)}
              canManageAssignment={canAssign}
            />
            {canReadInsights && <CustomerInsightsSection customerId={customer.id} />}
          </div>
        )
      case 'assessments':
        if (!canReadAssessments) return null
        return (
          <CustomerAssessmentsTab
            customer={customer}
            autoCreate={pendingCreate === 'assessments'}
            onAutoCreateHandled={clearPendingCreate}
          />
        )
      case 'documents':
        if (!canReadDocuments) return null
        return (
          <CustomerDocumentsTab
            customer={customer}
            autoCreate={pendingCreate === 'documents'}
            onAutoCreateHandled={clearPendingCreate}
          />
        )
      case 'reminders':
        if (!canReadReminders) return null
        return (
          <CustomerRemindersTab
            customer={customer}
            autoCreate={pendingCreate === 'reminders'}
            onAutoCreateHandled={clearPendingCreate}
          />
        )
      case 'notes':
        if (!canReadNotes) return null
        return (
          <CustomerNotesTab
            customer={customer}
            autoCreate={pendingCreate === 'notes'}
            onAutoCreateHandled={clearPendingCreate}
          />
        )
      case 'activity':
        return canReadActivity ? <CustomerActivityTab customer={customer} /> : null
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/customers')}
        className="btn btn-ghost btn-sm flex items-center gap-2 -ml-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Customers
      </button>

      {/* Header Card */}
      <div className="card card-elevated p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Customer Info */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-text-primary">{customer.fullName}</h1>
                {getStatusBadge(customer.status)}
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary flex-wrap">
                <code className="font-mono text-sm bg-surface-overlay px-2 py-0.5 rounded">
                  {customer.pan}
                </code>
                <span>{getOccupationLabel(customer.occupation)}</span>
                {customer.employerName && (
                  <>
                    <span className="text-text-tertiary">at</span>
                    <span>{customer.employerName}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <a
                  href={`mailto:${customer.emailPrimary}`}
                  className="flex items-center gap-1 text-primary-600 hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  {customer.emailPrimary}
                </a>
                <a
                  href={`tel:${customer.mobilePrimary}`}
                  className="flex items-center gap-1 text-primary-600 hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  +91 {customer.mobilePrimary}
                </a>
              </div>
            </div>
          </div>

          {/* Actions */}
          {(canUpdate || canDelete || canAssign) && (
            <div className="flex items-center gap-2">
              {canAssign && (
                <button
                  onClick={() => setIsAssignModalOpen(true)}
                  className="btn btn-outline btn-sm flex items-center gap-1.5"
                >
                  <UserCheck className="w-4 h-4 text-primary-600" />
                  <span>Assign Team</span>
                </button>
              )}
              {canUpdate && (
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="btn btn-primary btn-sm flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="btn btn-outline btn-sm btn-circle"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                {showActionsMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowActionsMenu(false)} />
                    <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-surface-dark rounded-lg shadow-lg border border-surface-overlay z-50">
                      <div className="py-1">
                        {canUpdate && (
                          <button
                            onClick={() => {
                              handleArchive()
                              setShowActionsMenu(false)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                          >
                            <Archive className="w-4 h-4" /> Archive Customer
                          </button>
                        )}
                        {canUpdate && canDelete && <hr className="my-1 border-surface-overlay" />}
                        {canDelete && (
                          <button
                            onClick={() => {
                              handleDelete()
                              setShowActionsMenu(false)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" /> Delete Customer
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-surface-overlay">
          <div className="text-center">
            <p className="text-2xl font-bold text-text-primary">{customer.totalAssessments}</p>
            <p className="text-sm text-text-secondary">Total Assessments</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {customer.pendingAssessments}
            </p>
            <p className="text-sm text-text-secondary">Pending Items</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {customer.totalAssessments -
                customer.pendingAssessments -
                customer.overdueAssessments}
            </p>
            <p className="text-sm text-text-secondary">Filed Returns</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {customer.overdueAssessments}
            </p>
            <p className="text-sm text-text-secondary">Overdue Items</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-surface-overlay">
        <nav className="flex gap-1 overflow-x-auto">
          {tabs
            .filter(tab => {
              if (tab.id === 'assessments') return canReadAssessments
              if (tab.id === 'documents') return canReadDocuments
              if (tab.id === 'reminders') return canReadReminders
              if (tab.id === 'notes') return canReadNotes
              if (tab.id === 'activity') return canReadActivity
              return true
            })
            .map(tab => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 -mb-px text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-surface-overlay'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="text-base">{renderTabContent()}</div>

      {/* Edit Modal */}
      {canUpdate && (
        <CustomerModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          customer={customer}
          onSuccess={() => setIsEditModalOpen(false)}
        />
      )}

      {/* Assign Team Modal */}
      {canAssign && (
        <AssignCustomerModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          customer={customer}
          onSuccess={() => setIsAssignModalOpen(false)}
        />
      )}
    </div>
  )
}
